import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppLayout    from '../../components/layout';
import Card         from '../../components/card';
import TextInput    from '../../components/input';
import Button       from '../../components/button';
import FileUploaderCV from '../../components/FileUploaderCV';
import api          from '../../utils/api';
import { config } from '../../config';

export default function CoachRegisterForm() {
  const navigate = useNavigate();
  const [form, setForm]     = useState({
    name: '', email: '', phone: '', specialties: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [error, setError]   = useState('');
  const [message, setMessage] = useState('');

  const handleChange = e =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.specialties || !cvFile) {
      setError('All fields except phone are required');
      return;
    }

    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('specialties', form.specialties);
      fd.append('cv', cvFile); // must match multer.single('cv')

      const { data } = await api.post('/api/auth/coach-register', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage(data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    }
  };

  // ——— Success view ———
  if (message) {
    return (
      <AppLayout>
        {/* Floating background blobs */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-24 left-10 w-72 h-72 bg-briefs-gradient-radial opacity-10 rounded-full animate-float" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-1" />
          <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-2" />
        </div>

        <div className="relative min-h-screen px-4">
          {/* Top-centered logo */}
          <div className="pt-8 pb-4 flex justify-center">
            <Link to="/" aria-label="B.R.I.E.F.S Home">
              <img
                src="/assets/no-back.png"
                alt="B.R.I.E.F.S"
                className="h-16 w-auto hover:opacity-90 transition-opacity"
              />
            </Link>
          </div>

          <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
            <Card title="Thank you for submitting">
              <p className="mb-3">{message}</p>
              <Button label="Back to Home" onClick={() => navigate('/')} />
            </Card>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ——— Form view ———
  return (
    <AppLayout>
      {/* Floating background blobs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-24 left-10 w-72 h-72 bg-briefs-gradient-radial opacity-10 rounded-full animate-float" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-1" />
        <div className="absolute bottom-20 left-1/3 w-64 h-64 bg-briefs-gradient-radial opacity-10 rounded-full animate-float float-delay-2" />
      </div>

      <div className="relative min-h-screen px-4">
        {/* Top-centered logo */}
        <div className="pt-8 pb-4 flex justify-center">
          <Link to="/" aria-label="B.R.I.E.F.S Home">
            <img
              src="/assets/no-back.png"
              alt="B.R.I.E.F.S"
              className="h-20 w-auto hover:opacity-90 transition-opacity"
            />
          </Link>
        </div>

        {/* Form card */}
        <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
          <Card title="Coach Registration" maxWidth="max-w-3xl">
            {error && <p className="text-red-500 mb-4 text-center">{error}</p>}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Row 1: Name | Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
                <TextInput
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Row 2: Phone | Specialties */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  label="Phone (optional)"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                />
                <TextInput
                  label="Specialties (comma-separated)"
                  name="specialties"
                  value={form.specialties}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* CV upload */}
              <div className="flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload CV (PDF)
                </label>
                <FileUploaderCV
                  name="cv"
                  accept="application/pdf"
                  file={cvFile}
                  onChange={e => setCvFile(e.target.files[0])}
                  required
                />
              </div>

              <Button label="Submit for Approval" type="submit" />
            </form>

            <p className="mt-4 text-center text-sm">
              Already a B.R.I.E.F.S Coach?{' '}
              <Link to={`${config.frontendUrl}/login`} className="text-blue-600 hover:underline">
                Login
              </Link>
            </p>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
